import AssignmentIcon from "@mui/icons-material/Assignment";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import FolderIcon from "@mui/icons-material/Folder";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import LinkIcon from "@mui/icons-material/Link";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import SendIcon from "@mui/icons-material/Send";
import StorageIcon from "@mui/icons-material/Storage";
import UpdateIcon from "@mui/icons-material/Update";
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { format } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { IForubusSignatureLogData } from "../../../../../models/forubus-signature-log";
import { ForubusSignatureFieldsRow } from "./ForubusSignatureFieldsRow";
import { QrCodeSignature } from "./QrCodeSignature";
import { ForubusSignatureSection } from "./ForubusSignatureSection";

interface IForubusSignatureDetailsModalProps {
  data: IForubusSignatureLogData;
  onClose: () => void;
}

const translationPath = "4uESign.page.logs.forubusSignatureLogs.details.";

const safeFormatDate = (
  value: string | null | undefined,
  locale: Locale,
): string => {
  if (!value) return "—";
  const dateStr = value.toString();
  if (dateStr.startsWith("0001-01-01") || dateStr === "0001-01-01T00:00:00")
    return "—";
  const date = new Date(value);
  if (isNaN(date.getTime()) || date.getFullYear() === 1) return "—";
  return format(date, "Pp", { locale }).replaceAll(",", "");
};

const formatFileSize = (bytes: number): string => {
  if (!bytes || bytes === 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const ForubusSignatureDetailsModal: React.FC<
  IForubusSignatureDetailsModalProps
> = ({ data, onClose }) => {
  const theme = useTheme();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const locale = language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es;

  const isSigned =
    !!data.finishDate &&
    !data.finishDate.startsWith("0001-01-01") &&
    data.finishDate !== "0001-01-01T00:00:00" &&
    !isNaN(new Date(data.finishDate).getTime()) &&
    new Date(data.finishDate).getFullYear() !== 1;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          overflow: "hidden",
          backgroundImage: "none",
          background: theme.palette.background.paper,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          pt: 2.5,
          pb: 2,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          background: `linear-gradient(135deg,
            ${alpha(theme.palette.primary.main, 0.14)} 0%,
            ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          flexShrink: 0,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.4 }}>
            <FingerprintIcon
              sx={{ color: theme.palette.primary.main, fontSize: 18 }}
            />
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, fontSize: "0.95rem", lineHeight: 1.2 }}
            >
              {t(translationPath + "title") || "Detalhes da Assinatura"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.disabled,
                fontFamily: "monospace",
                fontSize: "0.72rem",
              }}
            >
              ID #{data.identifier}
            </Typography>
            {data.signatureType && (
              <Box
                component="span"
                sx={{
                  px: 0.75,
                  py: 0.15,
                  borderRadius: 1,
                  background: alpha(theme.palette.primary.main, 0.12),
                  color: theme.palette.primary.main,
                  fontFamily: "monospace",
                  fontSize: "0.68rem",
                  fontWeight: 600,
                }}
              >
                {data.signatureType}
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 1,
          }}
        >
          <Tooltip title={t(translationPath + "close") || "Fechar"}>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.6,
              px: 1,
              py: 0.4,
              borderRadius: 1,
              border: `1px solid ${
                isSigned
                  ? alpha(theme.palette.success.main, 0.4)
                  : alpha(theme.palette.warning.main, 0.4)
              }`,
              background: isSigned
                ? alpha(theme.palette.success.main, 0.08)
                : alpha(theme.palette.warning.main, 0.08),
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                flexShrink: 0,
                background: isSigned
                  ? theme.palette.success.main
                  : theme.palette.warning.main,
                boxShadow: isSigned
                  ? `0 0 6px ${alpha(theme.palette.success.main, 0.7)}`
                  : `0 0 6px ${alpha(theme.palette.warning.main, 0.7)}`,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                fontSize: "0.65rem",
                letterSpacing: "0.04em",
                color: isSigned
                  ? theme.palette.success.main
                  : theme.palette.warning.main,
                lineHeight: 1,
              }}
            >
              {isSigned
                ? t(translationPath + "signed") || "Assinado"
                : t(translationPath + "pending") || "Pendente"}
            </Typography>
          </Box>
        </Box>
      </Box>

      <DialogContent
        sx={{
          px: 2,
          py: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": {
            background: alpha(theme.palette.primary.main, 0.2),
            borderRadius: 1,
          },
        }}
      >
        <ForubusSignatureSection
          title={
            t(translationPath + "section_identification") || "Identificação"
          }
          icon={<FingerprintIcon sx={{ fontSize: 14 }} />}
        >
          <ForubusSignatureFieldsRow
            icon={<AssignmentIcon fontSize="small" />}
            label={
              t(translationPath + "document_code") || "Código do Documento"
            }
            value={data.documentCode}
            mono
          />
          <ForubusSignatureFieldsRow
            icon={<DescriptionIcon fontSize="small" />}
            label={t(translationPath + "file_name") || "Nome do Arquivo"}
            value={data.fileName}
          />
          <ForubusSignatureFieldsRow
            icon={<FolderIcon fontSize="small" />}
            label={t(translationPath + "file_path") || "Caminho do Arquivo"}
            value={data.filePath}
            mono
          />
          <ForubusSignatureFieldsRow
            icon={<InsertDriveFileIcon fontSize="small" />}
            label={t(translationPath + "mime_type") || "Tipo MIME"}
            value={data.mimeType}
            mono
          />
          <ForubusSignatureFieldsRow
            icon={<StorageIcon fontSize="small" />}
            label={t(translationPath + "file_size") || "Tamanho do Arquivo"}
            value={formatFileSize(data.fileSize)}
          />
        </ForubusSignatureSection>

        <Divider sx={{ opacity: 0.7 }} />

        <ForubusSignatureSection
          title={t(translationPath + "section_integrations") || "Integrações"}
          icon={<LinkIcon sx={{ fontSize: 14 }} />}
        >
          <ForubusSignatureFieldsRow
            icon={<LinkIcon fontSize="small" />}
            label={
              t(translationPath + "first_integration_id") ||
              "ID Receita / Atestado"
            }
            value={data.firstIntegrationId}
            mono
            accent
          />
          <ForubusSignatureFieldsRow
            icon={<LinkIcon fontSize="small" />}
            label={
              t(translationPath + "second_integration_id") ||
              "Segunda Integração"
            }
            value={data.secondIntegrationId}
            mono
          />
          <ForubusSignatureFieldsRow
            icon={<LinkIcon fontSize="small" />}
            label={
              t(translationPath + "third_integration_id") ||
              "Terceira Integração"
            }
            value={data.thirdIntegrationId}
            mono
          />
          <ForubusSignatureFieldsRow
            icon={<FolderIcon fontSize="small" />}
            label={
              t(translationPath + "ged_integration_id") || "Integração GED"
            }
            value={data.gedIntegrationId}
            mono
          />
        </ForubusSignatureSection>

        <Divider sx={{ opacity: 0.7 }} />

        <ForubusSignatureSection
          title={t(translationPath + "section_dates") || "Datas"}
          icon={<CalendarTodayIcon sx={{ fontSize: 14 }} />}
        >
          <ForubusSignatureFieldsRow
            icon={<CalendarTodayIcon fontSize="small" />}
            label={t(translationPath + "created") || "Criado em"}
            value={safeFormatDate(data.created, locale)}
          />
          <ForubusSignatureFieldsRow
            icon={<UpdateIcon fontSize="small" />}
            label={t(translationPath + "last_modified") || "Última Modificação"}
            value={safeFormatDate(data.lastModified, locale)}
          />
          <ForubusSignatureFieldsRow
            icon={<SendIcon fontSize="small" />}
            label={t(translationPath + "send_date") || "Data de Envio"}
            value={safeFormatDate(data.sendDate, locale)}
          />
          <ForubusSignatureFieldsRow
            icon={<EventAvailableIcon fontSize="small" />}
            label={t(translationPath + "finish_date") || "Data de Conclusão"}
            value={safeFormatDate(data.finishDate, locale)}
          />
        </ForubusSignatureSection>

        {data.qrCode && (
          <>
            <Divider sx={{ opacity: 0.7 }} />
            <ForubusSignatureSection
              title={t(translationPath + "section_qrcode") || "QR Code"}
              icon={<QrCode2Icon sx={{ fontSize: 14 }} />}
            >
              <QrCodeSignature
                value={data.qrCode}
                label={t(translationPath + "qr_code") || "Dados do QR Code"}
              />
            </ForubusSignatureSection>
          </>
        )}
      </DialogContent>

      <Divider />
      <Box
        sx={{
          px: 3,
          py: 1.25,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: alpha(theme.palette.action.hover, 0.3),
          flexShrink: 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: theme.palette.text.disabled, fontSize: "0.65rem" }}
        >
          {t(translationPath + "record_info") ||
            "Registro de assinatura digital"}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.disabled,
            fontFamily: "monospace",
            fontSize: "0.65rem",
          }}
        >
          #{data.identifier}
        </Typography>
      </Box>
    </Dialog>
  );
};

export default ForubusSignatureDetailsModal;
